/*
 * NWTBAT: played-performances.js (show-calendar SOW; performance-note SOW)
 * Pure rules for canceled, played, sold-out, and on-sale performances, plus one clock.
 * Contracts: project/plans/2026-10-10-show-performance-calendar/docs/contracts/played-performances-api.md
 * and project/plans/2026-10-12-performance-note/docs/contracts/dom-contract.md (Script interfaces).
 * Classic script: window.PlayedPerformances in the browser, module.exports in Node.
 * The rule functions never read the DOM, the clock, or the network. startClock is the only clock reader.
 */
(function (root, factory) {
	const api = factory();
	if (typeof module === "object" && module.exports) module.exports = api;
	if (root) root.PlayedPerformances = api;
})(typeof window !== "undefined" ? window : null, function () {
	const MINUTE_MS = 60000;
	const DAY_MS = 86400000;

	function toMs(start) {
		if (start === null || start === undefined || start === "") return NaN;
		if (typeof start === "number") return start;
		if (typeof start !== "string") return NaN;
		return Date.parse(start);
	}

	function startOf(perf) {
		return perf.startMs !== undefined && perf.startMs !== null ? perf.startMs : perf.start;
	}

	function isPlayed(start, nowMs, graceMinutes) {
		const startMs = toMs(start);
		if (!Number.isFinite(startMs) || !Number.isFinite(nowMs)) return false;
		const grace = Number.isFinite(graceMinutes) && graceMinutes > 0 ? graceMinutes : 0;
		return nowMs >= startMs + grace * MINUTE_MS;
	}

	// Canceled comes first: a canceled performance never becomes "played" (design.md section 4.4).
	function performanceState(perf, nowMs, graceMinutes) {
		if (!perf || typeof perf !== "object") return "sold_out";
		if (perf.canceled === true) return "canceled";
		if (isPlayed(startOf(perf), nowMs, graceMinutes)) return "played";
		const variants = Array.isArray(perf.variants) ? perf.variants : [];
		return variants.some((v) => v && v.available === true) ? "on_sale" : "sold_out";
	}

	// The first variant on sale, in product order, or null. No full price type (performance-note SOW).
	function chooseTicketType(perf) {
		const variants = perf && Array.isArray(perf.variants) ? perf.variants : [];
		return variants.find((v) => v && v.available === true) || null;
	}

	function isDated(data) {
		return !!data && typeof data === "object" && data.dated === true &&
			Array.isArray(data.performances) && data.performances.length > 0;
	}

	function weekKey(dateKey, weekStart) {
		if (typeof dateKey !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(dateKey)) return null;
		const ms = Date.parse(dateKey + "T00:00:00Z");
		if (!Number.isFinite(ms)) return null;
		const dow = new Date(ms).getUTCDay();
		const back = weekStart === "monday" ? (dow + 6) % 7 : dow;
		return new Date(ms - back * DAY_MS).toISOString().slice(0, 10);
	}

	function firstOpenWeek(performances, nowMs, graceMinutes, weekStart) {
		if (!Array.isArray(performances)) return null;
		let first = null;
		performances.forEach((p) => {
			if (!p || !p.local) return;
			const start = startOf(p);
			if (!Number.isFinite(toMs(start)) || isPlayed(start, nowMs, graceMinutes)) return;
			const key = weekKey(p.local.dateKey, weekStart);
			if (key && (first === null || key < first)) first = key;
		});
		return first;
	}

	function startClock(callback, intervalMs) {
		if (typeof callback !== "function") return function stop() {};
		const every = Number.isFinite(intervalMs) && intervalMs > 0 ? intervalMs : MINUTE_MS;
		callback(Date.now());
		let id = setInterval(() => callback(Date.now()), every);
		return function stop() {
			if (id === null) return;
			clearInterval(id);
			id = null;
		};
	}

	return {
		isPlayed, performanceState, chooseTicketType, isDated,
		weekKey, firstOpenWeek, startClock, MINUTE_MS,
	};
});
